import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";
import { FileText, Clock, DollarSign, TrendingUp, CheckCircle2, Sparkles, RefreshCw, Gift, Zap, ArrowRight, Brush, Paintbrush, Layers as LayersIcon, HardHat, Wrench } from "lucide-react";
import { PageHeader, Badge, Spinner } from "../components/Brand";
import OnboardingModal from "../components/OnboardingModal";
import { useApp, API } from "../AppContext";
import { Link, useNavigate } from "react-router-dom";
import { usePrefetch } from "../hooks/usePrefetch";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/billing/me`).catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setSub(b.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try { await axios.post(`${API}/seed-demo`); await load(); } finally { setSeeding(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Spinner /></div>;

  const kpis = [
    { label: t.dashboard.kpi.total, value: stats.total_quotes, icon: FileText, color: "#D4AF37" },
    { label: t.dashboard.kpi.pending, value: stats.pending, icon: Clock, color: "#7BAEFA" },
    { label: t.dashboard.kpi.revenue, value: fmt(stats.revenue), icon: DollarSign, color: "#4ADE80" },
    { label: t.dashboard.kpi.conversion, value: `${stats.conversion}%`, icon: TrendingUp, color: "#D4AF37" },
    { label: t.dashboard.kpi.approved, value: stats.approved, icon: CheckCircle2, color: "#4ADE80" },
    { label: t.dashboard.kpi.avg, value: fmt(stats.avg_value), icon: Sparkles, color: "#C00000" },
  ];

  const monthly = stats.monthly?.length ? stats.monthly : [
    { month: "2025-09", amount: 0 }, { month: "2025-10", amount: 0 }, { month: "2025-11", amount: 0 },
    { month: "2025-12", amount: 0 }, { month: "2026-01", amount: 0 }, { month: "2026-02", amount: 0 },
  ];

  return (
    <div>
      <OnboardingModal />
      <PageHeader title={t.dashboard.title} subtitle={t.dashboard.welcome}
        action={
          <button onClick={seed} disabled={seeding} className="btn-ghost" data-testid="dashboard-seed">
            {seeding ? <Spinner /> : <RefreshCw size={14} />} {t.dashboard.seed}
          </button>
        }
      />

      {/* Trial / quota banner */}
      {sub && !sub.active && (
        <Link to="/app/billing" className="block mb-6" data-testid="dashboard-trial-banner">
          <div className="p-4 rounded-2xl flex items-center justify-between gap-4 hover:brightness-110 transition cursor-pointer flex-wrap"
            style={{
              background: sub.in_pro_trial
                ? "linear-gradient(135deg, rgba(74,222,128,0.12), rgba(212,175,55,0.08))"
                : sub.trial_quotes_remaining > 0
                ? "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(192,0,0,0.06))"
                : "linear-gradient(135deg, rgba(192,0,0,0.18), rgba(212,175,55,0.06))",
              border: "1px solid rgba(212,175,55,0.3)",
            }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: sub.in_pro_trial ? "rgba(74,222,128,0.2)" : "rgba(212,175,55,0.2)" }}>
                {sub.in_pro_trial ? <Clock size={18} color="#4ADE80" /> : sub.trial_quotes_remaining > 0 ? <Zap size={18} color="#D4AF37" /> : <Gift size={18} color="#D4AF37" />}
              </div>
              <div>
                <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>
                  {sub.in_pro_trial
                    ? (lang === "fr" ? "Essai Pro actif" : "Pro trial active")
                    : sub.trial_quotes_remaining > 0
                    ? (lang === "fr" ? `${sub.trial_quotes_remaining} soumission${sub.trial_quotes_remaining > 1 ? "s" : ""} d'essai restante${sub.trial_quotes_remaining > 1 ? "s" : ""}` : `${sub.trial_quotes_remaining} free quote${sub.trial_quotes_remaining > 1 ? "s" : ""} left`)
                    : (lang === "fr" ? "Vos soumissions gratuites sont épuisées" : "Your free quotes are used up")}
                </div>
                <div className="text-xs" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {sub.in_pro_trial
                    ? (lang === "fr" ? `Toutes les fonctionnalités Pro débloquées · ${Math.max(0, Math.ceil((new Date(sub.pro_trial_until) - new Date()) / 86400000))} jour(s) restant(s)` : `All Pro features unlocked · ${Math.max(0, Math.ceil((new Date(sub.pro_trial_until) - new Date()) / 86400000))} day(s) left`)
                    : !sub.pro_trial_used
                    ? (lang === "fr" ? "Démarrez votre essai Pro gratuit de 14 jours — aucune carte requise" : "Start your free 14-day Pro trial — no card required")
                    : (lang === "fr" ? "Abonnez-vous pour continuer à créer des soumissions" : "Subscribe to keep creating quotes")}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm font-bold" style={{ color: "#D4AF37" }}>
              {sub.in_pro_trial
                ? (lang === "fr" ? "Voir" : "View")
                : !sub.pro_trial_used
                ? (lang === "fr" ? "Activer" : "Activate")
                : (lang === "fr" ? "S'abonner" : "Subscribe")}
              <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {kpis.map((k, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="kpi-card" data-testid={`kpi-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <k.icon size={18} color={k.color} />
            </div>
            <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{k.label}</div>
            <div className="font-heading text-2xl font-bold" style={{ color: "#F5F5F5" }}>{k.value}</div>
          </motion.div>
        ))}
      </div>

      {/* PROGÈRE Vision — Big hero CTA (accessible en 1 clic depuis Dashboard) */}
      <Link to="/app/quotes/new/vision" data-testid="vision-hero-cta"
        className="block mb-6 rounded-3xl overflow-hidden relative group transition active:scale-[0.99]">
        <div className="relative p-6 sm:p-8"
          style={{
            background: "linear-gradient(135deg, #0B0C0F 0%, #1a0f0a 50%, #0B0C0F 100%)",
            border: "1px solid rgba(212,175,55,0.4)",
            boxShadow: "0 12px 40px rgba(212,175,55,0.15), 0 0 0 1px rgba(212,175,55,0.08) inset",
          }}>
          {/* Halo effects */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }} />
          <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, #C00000 0%, transparent 70%)" }} />

          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 relative"
              style={{
                background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
                boxShadow: "0 8px 24px rgba(212,175,55,0.4), 0 0 0 4px rgba(11,12,15,0.6)",
              }}>
              <Sparkles size={28} color="#0B0C0F" strokeWidth={2.5} />
              <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black"
                style={{ background: "#C00000", color: "#F5F5F5", border: "2px solid #0B0C0F" }}>IA</span>
            </div>

            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest mb-2"
                style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.35)" }}>
                <Sparkles size={10} /> {lang === "fr" ? "Nouveau · Ancré Québec 2026" : "New · Anchored Quebec 2026"}
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black leading-tight" style={{ color: "#F5F5F5" }}>
                {lang === "fr" ? "PROGÈRE Vision IA" : "PROGÈRE Vision AI"}
              </h2>
              <p className="text-sm mt-1.5" style={{ color: "rgba(245,245,245,0.75)" }}>
                {lang === "fr"
                  ? "Prenez une photo → 3 rendus IA → estimation détaillée en moins de 60 secondes. Prix ancrés sur le catalogue Québec 2026 (CCQ + 112 items)."
                  : "Take a photo → 3 AI renders → detailed estimate in under 60 seconds. Anchored on Quebec 2026 catalog (CCQ + 112 items)."}
              </p>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "rgba(245,245,245,0.6)" }}>
                  <CheckCircle2 size={12} color="#4ADE80" /> {lang === "fr" ? "30 espaces" : "30 spaces"}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "rgba(245,245,245,0.6)" }}>
                  <CheckCircle2 size={12} color="#4ADE80" /> {lang === "fr" ? "Multi-photos" : "Multi-photos"}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "rgba(245,245,245,0.6)" }}>
                  <CheckCircle2 size={12} color="#4ADE80" /> {lang === "fr" ? "Devis brouillon 1-clic" : "1-click draft quote"}
                </span>
              </div>
            </div>

            <div className="flex-shrink-0 self-stretch flex items-center">
              <div className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-bold text-sm transition group-hover:brightness-110"
                style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0C0F", boxShadow: "0 8px 24px rgba(212,175,55,0.4)" }}>
                <Sparkles size={16} strokeWidth={2.5} />
                <span>{lang === "fr" ? "Lancer Vision" : "Launch Vision"}</span>
                <ArrowRight size={16} className="transition group-hover:translate-x-1" strokeWidth={2.5} />
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Quick templates — pre-fill AI prompt and jump to QuoteBuilder */}
      <QuickTemplatesBar lang={lang} nav={nav} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="glass-card p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#F5F5F5" }}>{t.dashboard.monthly}</h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#C00000" stopOpacity={0.65} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(245,245,245,0.05)" />
                <XAxis dataKey="month" stroke="rgba(245,245,245,0.4)" fontSize={11} />
                <YAxis stroke="rgba(245,245,245,0.4)" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12 }} />
                <Bar dataKey="amount" fill="url(#g1)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} color="#D4AF37" />
            <h3 className="font-heading text-lg font-semibold" style={{ color: "#F5F5F5" }}>{t.dashboard.ai_tips}</h3>
          </div>
          <ul className="space-y-3">
            {t.dashboard.tips.map((tip, i) => (
              <li key={i} className="text-sm p-3 rounded-2xl" style={{ background: "rgba(212,175,55,0.06)", color: "rgba(245,245,245,0.85)", border: "1px solid rgba(212,175,55,0.15)" }}>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <AISuggestionsWidget lang={lang} />

      <div className="glass-card p-6">
        <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#F5F5F5" }}>{t.dashboard.recent}</h3>
        {stats.recent?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uppercase text-xs tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>
                  <th className="py-3">{t.quotes.number}</th><th>{t.quotes.client}</th><th>{t.quotes.project}</th>
                  <th>{t.quotes.total}</th><th>{t.quotes.status}</th><th></th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((q) => (
                  <tr key={q.id} className="border-t" style={{ borderColor: "rgba(245,245,245,0.05)" }}>
                    <td className="py-3 font-mono text-xs" style={{ color: "#D4AF37" }}>{q.quote_number}</td>
                    <td>{q.client_name}</td>
                    <td className="max-w-xs truncate">{q.project_name}</td>
                    <td className="font-semibold">{fmt(q.total)}</td>
                    <td><Badge status={q.status} label={t.status[q.status] || q.status} /></td>
                    <td><Link to={`/app/quotes/${q.id}`} className="text-xs" style={{ color: "#D4AF37" }} data-testid={`open-quote-${q.id}`}>→</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm py-8 text-center" style={{ color: "rgba(245,245,245,0.5)" }}>{t.quotes.empty}</p>
        )}
      </div>
    </div>
  );
}

// ============ AI SUGGESTIONS WIDGET ============
// Detects pending quotes older than 7 days and surfaces them as a proactive nudge.
function AISuggestionsWidget({ lang }) {
  const { quotes } = usePrefetch();
  const SEVEN_DAYS_MS = 7 * 86400000;
  const now = Date.now();
  const stale = (quotes || [])
    .filter((q) => (q.status === "sent" || q.status === "pending"))
    .filter((q) => {
      const d = q.date || q.created_at;
      if (!d) return false;
      return now - new Date(d).getTime() > SEVEN_DAYS_MS;
    })
    .sort((a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at))
    .slice(0, 3);

  if (stale.length === 0) return null;

  const daysAgo = (d) => Math.floor((now - new Date(d).getTime()) / 86400000);
  return (
    <div className="glass-strong p-5 mb-6" data-testid="ai-suggestions">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} color="#D4AF37" />
        <h3 className="font-heading text-sm uppercase tracking-wider font-semibold" style={{ color: "#D4AF37" }}>
          {lang === "fr" ? "Suggestions de l'IA" : "AI Suggestions"}
        </h3>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ml-1"
          style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}>
          {stale.length}
        </span>
      </div>
      <div className="space-y-2">
        {stale.map((q) => {
          const days = daysAgo(q.date || q.created_at);
          return (
            <Link key={q.id} to={`/app/quotes/${q.id}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.03] transition cursor-pointer"
              style={{ border: "1px solid rgba(245,245,245,0.06)" }}
              data-testid={`ai-stale-${q.id}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: days > 14 ? "rgba(192,0,0,0.15)" : "rgba(212,175,55,0.12)" }}>
                <Clock size={15} color={days > 14 ? "#FF6B6B" : "#D4AF37"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: "#F5F5F5" }}>
                  {lang === "fr"
                    ? `Relancez ${q.client_name} — devis envoyé il y a ${days} jours`
                    : `Follow up with ${q.client_name} — quote sent ${days} days ago`}
                </div>
                <div className="text-xs truncate" style={{ color: "rgba(245,245,245,0.55)" }}>
                  {q.project_name} · {fmt(q.total)} · #{q.quote_number}
                </div>
              </div>
              <div className="text-xs font-bold flex items-center gap-1" style={{ color: "#D4AF37" }}>
                {lang === "fr" ? "Relancer" : "Follow up"} <ArrowRight size={12} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ============ QUICK TEMPLATES BAR ============
// One-click presets that prefill the AI prompt and jump to /app/quotes/new.
const QUICK_TEMPLATES = [
  { id: "drywall", icon: LayersIcon, fr: "Gypse", en: "Drywall",
    promptFr: "Installation de gypse 1/2\" standard sur 2000 pi² avec tirage de joints niveau 4, primer et 2 couches de peinture. Bureau commercial à Montréal.",
    promptEn: "Install standard 1/2\" drywall over 2000 sq.ft. with level-4 joint compound, primer and 2 paint coats. Commercial office in Montréal." },
  { id: "paint", icon: Paintbrush, fr: "Peinture", en: "Painting",
    promptFr: "Peinture intérieure 2 couches Benjamin Moore sur 1800 pi² (murs + plafonds), apprêt inclus, condo 3 chambres à Laval.",
    promptEn: "Interior painting 2 coats Benjamin Moore on 1800 sq.ft (walls + ceilings), primer included, 3-bedroom condo in Laval." },
  { id: "cleanup", icon: Brush, fr: "Nettoyage", en: "Cleanup",
    promptFr: "Nettoyage après construction d'un local commercial 4500 pi² incluant planchers, vitres, dépoussiérage complet et gestion des déchets.",
    promptEn: "Post-construction cleanup of a 4500 sq.ft commercial space including floors, windows, full dusting and waste management." },
  { id: "floor", icon: HardHat, fr: "Plancher", en: "Flooring",
    promptFr: "Pose de plancher LVT (vinyle de luxe) sur 1200 pi² avec préparation sous-plancher (nivellement Mapei), résidentiel haut de gamme à Boucherville.",
    promptEn: "LVT (luxury vinyl) flooring install over 1200 sq.ft with subfloor prep (Mapei leveling), high-end residential in Boucherville." },
  { id: "sub", icon: Wrench, fr: "Sous-traitance", en: "Subcontract",
    promptFr: "Sous-traitance électrique pour rénovation cuisine + salle de bain (rajout de circuits, luminaires DEL, prises USB-C), maison à Québec.",
    promptEn: "Electrical subcontract for kitchen + bathroom reno (extra circuits, LED fixtures, USB-C outlets), house in Québec City." },
];

function QuickTemplatesBar({ lang, nav }) {
  const start = (tpl) => {
    try {
      localStorage.setItem("progere_quick_prompt", lang === "fr" ? tpl.promptFr : tpl.promptEn);
    } catch {/* ignore */}
    nav("/app/quotes/new");
  };
  return (
    <div className="mb-8" data-testid="quick-templates-bar">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={16} color="#D4AF37" />
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.7)" }}>
          {lang === "fr" ? "Soumissions 1 clic" : "1-click quotes"}
        </h3>
        <span className="text-xs ml-2" style={{ color: "rgba(245,245,245,0.45)" }}>
          {lang === "fr" ? "Pré-rempli pour l'IA — vous gagnez 5 minutes par devis" : "Pre-filled for the AI — save 5 minutes per quote"}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {QUICK_TEMPLATES.map((tpl) => (
          <button key={tpl.id} onClick={() => start(tpl)}
            className="glass-card p-4 flex flex-col items-start gap-2 text-left hover:scale-[1.02] transition cursor-pointer"
            style={{ borderColor: "rgba(212,175,55,0.18)" }}
            data-testid={`quick-tpl-${tpl.id}`}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
              <tpl.icon size={16} color="#D4AF37" />
            </div>
            <div className="text-sm font-semibold" style={{ color: "#F5F5F5" }}>
              {lang === "fr" ? tpl.fr : tpl.en}
            </div>
            <div className="text-[10px] uppercase tracking-widest flex items-center gap-1" style={{ color: "rgba(212,175,55,0.7)" }}>
              <Sparkles size={9} /> {lang === "fr" ? "Démarrer" : "Start"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
